import React, { lazy, Suspense, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { ShareVideoResponse } from '../interfaces/videoInterface';
import { getVideo, getVideos } from '../services/videoService';

import { Loader } from '../components/common/Loader';
import { PoweredByMyinterview } from '../components/common/PoweredByMyinterview';
import { CANDIDATE_ITEM_HEIGHT, CANDIDATE_ITEM_PADDING, CANDIDATE_ITEM_THUMB_BORDER_WIDTH, CANDIDATE_ITEM_THUMB_SIZE, CANDIDATE_LIST_Y_PADDING, CANDIDATE_OVERVIEW_HEIGHT, HEADER_HEIGHT } from '../constants/ui.constants';
import { serializeVideosToCep } from '../utils/serialize.utils';

const ShareVideo = lazy(() => import('./ShareVideo'));
const SmartShortlist = lazy(() => import('./SmartShortlist'));
const CandidateView = lazy(() => import('./CandidateView'));
const FailLoad = lazy(() => import('./FailLoad'));

export const OVERVIEW_TOP = 2 * CANDIDATE_LIST_Y_PADDING + 2 * CANDIDATE_ITEM_PADDING + CANDIDATE_ITEM_THUMB_SIZE;

const Main: React.FC = () => {
  const [videosData, setVideosData] = useState<ShareVideoResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { shareCode, shortlistShareCode } = useParams();

  const setVideo = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const data = shareCode
        ? await getVideo(shareCode || '')
        : await getVideos(shortlistShareCode || '');

      data.videos = serializeVideosToCep(data.videos);
      setVideosData(data);
    } catch (err) {
      setVideosData(null);
      setErrorMessage('There was an error. Please try again or refresh.');
    } finally {
      setIsLoading(false);
    }
  };

  const headerText = videosData && `Review ${videosData.shareSettings.newSharePage ? 'Candidate' : 'Video'}${(videosData.videos || []).length !== 1 ? 's' : ''}`;

  const elementSelector = (): JSX.Element | null => {
    if (!videosData) return <FailLoad setVideo={setVideo} />;
    if (videosData.shareSettings.newSharePage) return <CandidateView videoData={videosData} />;

    if (shortlistShareCode) return <SmartShortlist videoData={videosData} shareCode={shortlistShareCode || ''} refetch={setVideo} />;
    return <ShareVideo videoData={videosData} />;
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--header-height', `${HEADER_HEIGHT}px`);
    document.documentElement.style.setProperty('--candidate-list-y-padding', `${CANDIDATE_LIST_Y_PADDING}px`);
    document.documentElement.style.setProperty('--candidate-item-height', `${CANDIDATE_ITEM_HEIGHT}px`);
    document.documentElement.style.setProperty('--candidate-item-padding', `${CANDIDATE_ITEM_PADDING}px`);
    document.documentElement.style.setProperty('--candidate-item-thumb-border-width', `${CANDIDATE_ITEM_THUMB_BORDER_WIDTH}px`);
    document.documentElement.style.setProperty('--candidate-overview-top', `${OVERVIEW_TOP}px`);
    document.documentElement.style.setProperty('--candidate-overview-height', `${CANDIDATE_OVERVIEW_HEIGHT}px`);
    document.documentElement.style.setProperty('--candidate-thumb-size', `${CANDIDATE_ITEM_THUMB_SIZE}px`);
    setVideo();
  }, []);

  return (
    <div className="main-content-container flex column full">
      <Header labelText={headerText ?? ''} logoSrc={videosData ? videosData.logo || 'https://logo.clearbit.com/myinterview.com' : ''} isLoading={isLoading} />
      {isLoading ? <Loader /> : <Suspense fallback={React.Fragment}>{elementSelector()}</Suspense>}
      <PoweredByMyinterview />
    </div>
  );
};

export default Main;
